/**
 * EForm 日期欄位共用工具
 * - 設計器格式代碼（如 DD/MM/YYYY）與 Flatpickr dateFormat、dayjs 的對照
 * - ISO (YYYY-MM-DD) 與顯示格式的 parse/format，避免 toLocaleDateString
 */

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/** 設計器支援的日期格式代碼 */
export const EFORM_DATE_FORMATS = [
  'YYYY-MM-DD',
  'MM/DD/YYYY',
  'DD/MM/YYYY',
  'YYYY/MM/DD',
  'MM-DD-YYYY',
  'DD-MM-YYYY'
];

/** 設計器格式 -> Flatpickr dateFormat (e.g. d/m/Y) */
const FORMAT_TO_FLATPICKR = {
  'YYYY-MM-DD': 'Y-m-d',
  'MM/DD/YYYY': 'm/d/Y',
  'DD/MM/YYYY': 'd/m/Y',
  'YYYY/MM/DD': 'Y/m/d',
  'MM-DD-YYYY': 'm-d-Y',
  'DD-MM-YYYY': 'd-m-Y'
};

/** 設計器格式 -> dayjs format (用於 format/parse) */
const FORMAT_TO_DAYJS = {
  'YYYY-MM-DD': 'YYYY-MM-DD',
  'MM/DD/YYYY': 'MM/DD/YYYY',
  'DD/MM/YYYY': 'DD/MM/YYYY',
  'YYYY/MM/DD': 'YYYY/MM/DD',
  'MM-DD-YYYY': 'MM-DD-YYYY',
  'DD-MM-YYYY': 'DD-MM-YYYY'
};

/**
 * 取得 Flatpickr 的 dateFormat 選項
 * @param {string} formatKey - 設計器格式，如 'DD/MM/YYYY'
 * @returns {string} Flatpickr 格式，如 'd/m/Y'
 */
export function getFlatpickrDateFormat(formatKey) {
  return FORMAT_TO_FLATPICKR[formatKey] || 'Y-m-d';
}

/**
 * 將 ISO 日期字串 (YYYY-MM-DD) 格式化成顯示字串
 * @param {string} isoDate - 如 '2025-12-01'
 * @param {string} formatKey - 如 'DD/MM/YYYY'
 * @returns {string} 顯示字串，如 '01/12/2025'
 */
export function formatDateForDisplay(isoDate, formatKey) {
  if (!isoDate || !formatKey) return '';
  const dayjsFormat = FORMAT_TO_DAYJS[formatKey] || 'YYYY-MM-DD';
  const d = dayjs(isoDate, 'YYYY-MM-DD', true);
  return d.isValid() ? d.format(dayjsFormat) : '';
}

/**
 * 將顯示字串依指定格式解析為 ISO (YYYY-MM-DD)
 * @param {string} displayValue - 如 '01/12/2025'
 * @param {string} formatKey - 如 'DD/MM/YYYY'
 * @returns {string} ISO 字串或空字串
 */
export function parseDisplayToIso(displayValue, formatKey) {
  if (!displayValue || !formatKey) return '';
  const dayjsFormat = FORMAT_TO_DAYJS[formatKey] || 'YYYY-MM-DD';
  const d = dayjs(displayValue, dayjsFormat, true);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
}

/**
 * 判斷字串是否為 ISO 日期 (YYYY-MM-DD)
 * @param {string} value
 * @returns {boolean}
 */
export function isIsoDateString(value) {
  if (!value || typeof value !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
}

/**
 * 將 Date 轉為本地日期的 YYYY-MM-DD（不用 UTC，避免時區差一天）
 * @param {Date} date
 * @returns {string}
 */
export function dateToLocalIsoString(date) {
  if (!date || !(date instanceof Date)) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * 偵測是否為「誤用年前兩碼當日」的錯誤 value（如 2026-03-06 被存成 20/06/2026）
 * 若 value 為 XX/YY/YYYY 且 XX === YYYY 前兩碼，則可能是此 bug，應清空不顯示
 * @param {string} value - 目前 value
 * @param {string} formatKey - 如 'DD/MM/YYYY'
 * @returns {boolean}
 */
export function isLikelyCorruptedDateValue(value, formatKey) {
  if (!value || typeof value !== 'string') return false;
  const v = value.trim();
  const slashParts = v.split('/');
  if (slashParts.length !== 3) return false;
  const [a, b, c] = slashParts.map(s => s.trim());
  if (a.length !== 2 || c.length !== 4) return false;
  const yearPrefix = c.substring(0, 2);
  return a === yearPrefix;
}

/** Flatpickr CSS CDN（供 iframe 內日曆顯示用） */
const FLATPICKR_CSS_URL = 'https://cdn.jsdelivr.net/npm/flatpickr@4.6.13/dist/flatpickr.min.css';
const FLATPICKR_JS_URL = 'https://cdn.jsdelivr.net/npm/flatpickr@4.6.13/dist/flatpickr.min.js';

/**
 * 若 container 屬於 iframe，在該 document 的 head 注入 Flatpickr CSS，使日曆能正確顯示
 * @param {HTMLElement} container - 表單容器（可能是 iframe.contentDocument.body）
 */
export function ensureFlatpickrStylesInDocument(container) {
  if (!container || !container.ownerDocument) return;
  const doc = container.ownerDocument;
  if (doc === document) return;
  if (doc.querySelector('link[href*="flatpickr"]')) return;
  const head = doc.head || doc.getElementsByTagName('head')[0];
  if (!head) return;
  const link = doc.createElement('link');
  link.rel = 'stylesheet';
  link.href = FLATPICKR_CSS_URL;
  head.appendChild(link);
}

/**
 * 在 iframe 的 document 內注入腳本：載入 Flatpickr 並對 input[data-input-type="date"] 初始化。
 * 日曆在 iframe 內建立，無跨 document 問題。
 * @param {Document} iframeDoc - iframe.contentDocument
 */
export function injectFlatpickrIntoIframe(iframeDoc) {
  if (!iframeDoc || !iframeDoc.head) return;
  if (iframeDoc.querySelector('script[data-eform-flatpickr-injected]')) return;
  const formatMap = {
    'YYYY-MM-DD': 'Y-m-d',
    'MM/DD/YYYY': 'm/d/Y',
    'DD/MM/YYYY': 'd/m/Y',
    'YYYY/MM/DD': 'Y/m/d',
    'MM-DD-YYYY': 'm-d-Y',
    'DD-MM-YYYY': 'd-m-Y'
  };
  const script = iframeDoc.createElement('script');
  script.setAttribute('data-eform-flatpickr-injected', '1');
  script.textContent = `
(function() {
  var formatMap = ${JSON.stringify(formatMap)};
  function isIso(s) { return s && typeof s === 'string' && /^\\d{4}-\\d{2}-\\d{2}$/.test(s.trim()); }
  function dateToLocalIso(d) {
    if (!d || !d.getFullYear) return '';
    var y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }
  var link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = '${FLATPICKR_CSS_URL}';
  document.head.appendChild(link);
  var s = document.createElement('script');
  s.src = '${FLATPICKR_JS_URL}';
  s.onload = function() {
    document.querySelectorAll('input[data-input-type="date"]').forEach(function(el) {
      var formatKey = el.getAttribute('data-date-format') || 'YYYY-MM-DD';
      var fpFormat = formatMap[formatKey] || 'Y-m-d';
      var dataVal = (el.getAttribute('data-value') || '').trim();
      var raw = (el.value || el.getAttribute('value') || '').trim();
      var defaultDateStr = (dataVal && isIso(dataVal)) ? dataVal : (raw || null);
      var defaultDateObj = null;
      if (defaultDateStr && isIso(defaultDateStr)) {
        var d = new Date(defaultDateStr + 'T12:00:00');
        if (!isNaN(d.getTime())) defaultDateObj = d;
      }
      var opt = { dateFormat: fpFormat, allowInput: true, defaultDate: defaultDateObj || defaultDateStr || undefined };
      opt.onChange = function(selectedDates, dateStr) {
        el.setAttribute('value', dateStr);
        if (selectedDates[0]) el.setAttribute('data-value', dateToLocalIso(selectedDates[0]));
      };
      try { if (typeof flatpickr !== 'undefined') flatpickr(el, opt); } catch (e) { console.warn('[eform] flatpickr init', e); }
    });
  };
  document.head.appendChild(s);
})();
`;
  iframeDoc.head.appendChild(script);
}
